import { useState, useContext, useRef, useEffect } from "react";
import {
  Box,
  IconButton,
  useTheme,
  Card,
  InputBase,
  Typography,
  Grid,
  Popper,
  ClickAwayListener,
  Divider,
} from "@mui/material";
import {
  LightModeOutlined as LightModeOutlinedIcon,
  DarkModeOutlined as DarkModeOutlinedIcon,
  NotificationsOutlined as NotificationsOutlinedIcon,
  SettingsOutlined as SettingsOutlinedIcon,
  PersonOutlined as PersonOutlinedIcon,
  Apps as AppsIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  HomeOutlined as HomeOutlinedIcon,
} from "@mui/icons-material";

import { ColorModeContext, tokens } from "../../theme";
import { useNavigate, useLocation } from "react-router-dom";
import SideNav from "./SideNav";
import UserMenu from "./UserMenu";
import { useModule } from "../../context/ModuleContext";
import { iepaProd } from "../../constants/global";

const Topbar = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const colorMode = useContext(ColorModeContext);
  const [isSideNavOpen, setIsSideNavOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [modules, setModules] = useState([]);
  const [additionalApps, setAdditionalApps] = useState([]);
  const [accessibleModules, setAccessibleModules] = useState([]);

  const sideNavRef = useRef(null);
  const userMenuRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { setSelectedModule } = useModule();

  // Fetch modules on first click
  const handleIconClick = async (event) => {
    const newAnchor = anchorEl ? null : event.currentTarget;
    setAnchorEl(newAnchor);

    if (!anchorEl) {
      try {
        const response = await fetch(
          `${iepaProd.iepaUserSrvAPI}/iepa/api/modules/org/1`
        );
        const data = await response.json();

        setModules(
          data.filter((mod) => mod.moduleEntityType?.entityName === "Application")
        );

        setAdditionalApps(
          data.filter((mod) => mod.moduleEntityType?.entityName !== "Application")
        );
      } catch (error) {
        console.error("Error fetching modules:", error);
      }
    }
  };



  const toggleSideNav = () => setIsSideNavOpen((prev) => !prev);
  const toggleUserMenu = () => setIsUserMenuOpen((prev) => !prev);

  const handleMouseEnter = (e) => {
    if (sideNavRef.current?.contains(e.target)) setIsSideNavOpen(true);
    if (userMenuRef.current?.contains(e.target)) setIsUserMenuOpen(true);
  };

  const handleMouseLeave = (e) => {
    if (!sideNavRef.current?.contains(e.target)) setIsSideNavOpen(false);
    if (!userMenuRef.current?.contains(e.target)) setIsUserMenuOpen(false);
  };

  const goToModulesLandingPage = () => {
    if (location.pathname === "/ModulesLandingPage") {
      navigate("/Dashboard");
    } else {
      navigate("/ModulesLandingPage");
    }
  };

  const goToDashboard = (tabName) => {
    navigate("/Dashboard", { state: { tabName } });
  };

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      p={2}
      backgroundColor={colors.primary[400]}
      sx={{
        boxShadow: "3px 1px 10px #a5a6a8",
        zIndex: 1100,
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
      }}
    >
      {/* Left Section: Apps Icon */}
      <Box display="flex" sx={{ gap: 1 }}>
        <IconButton onClick={handleIconClick}>
          <AppsIcon />
        </IconButton>
      </Box>

      {/* Popper for Apps */}
      <Popper
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        placement="bottom-start"
        modifiers={[{ name: "offset", options: { offset: [0, 8] } }]}
        style={{ zIndex: 1300 }}
      >
        <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
          <Card
            sx={{
              width: 320,
              maxHeight: 400,
              overflowY: "auto",
              p: 2,
              boxShadow: 6,
              borderRadius: 2,
              "&::-webkit-scrollbar": {
                width: "12px",          // 👈 increase width
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#e0e0e0",
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#98bde4ff",
                borderRadius: "10px",
                border: "3px solid #e0e0e0",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: "#757575ff",
              },
            }}
          >
            <Grid container spacing={1}>
              {modules.map((mod, index) => (
                <Grid item xs={4} key={index}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 1,
                      borderRadius: 1,
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: "#f5f5f5",
                        transform: "scale(1.15)",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      },
                    }}
                    onClick={() => {
                      setSelectedRole(mod.name);
                      setSelectedModule(mod);
                      setAnchorEl(null);

                      if (["Orders", "Delivery", "Collections"].includes(mod.name)) {
                        goToDashboard(mod.name);
                      } else if (mod.uriPath) {
                        navigate(mod.uriPath);
                      }
                    }}
                  >
                    <img
                      src={`/assets${mod.imagePath}.png`}
                      alt={mod.name}
                      style={{ width: 36, height: 36, objectFit: "contain" }}
                    />
                    <Typography variant="caption" align="center">
                      {mod.name}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ mb: 1 }} />

            <Grid container spacing={1}>
              {additionalApps.map((app, index) => (
                <Grid item xs={4} key={index}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 1,
                      borderRadius: 1,
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: "#f5f5f5",
                        transform: "scale(1.15)",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      },
                    }}
                    onClick={() => {
                      setSelectedRole(app.name);
                      setAnchorEl(null);
                      setSelectedModule(app);

                      if (app.name === "Configurations") {
                        navigate("/configurations?mode=configurations");
                      } else if (app.name === "Maintenance") {
                        navigate("/configurations?mode=maintenance");
                      } else if (app.name === "Collection") {
                        goToDashboard("Collection");
                      } else if (app.name === "Tools") {
                        navigate(app.uriPath);
                      }
                    }}
                  >
                    <img
                      src={`/assets${app.imagePath}.png`}
                      alt={app.name}
                      style={{ width: 36, height: 36, objectFit: "contain" }}
                    />
                    <Typography variant="caption" align="center">
                      {app.name}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Card>
        </ClickAwayListener>
      </Popper>

      {/* Search Bar */}
      <Box
        backgroundColor={colors.primary[400]}
        borderRadius="10px"
        border="1px solid #a5a6a8"
        display="flex"
      >
        <InputBase sx={{ ml: 2, flex: 1 }} placeholder="Search" />
        <IconButton type="button" sx={{ p: 1 }}>
          <SearchIcon />
        </IconButton>
      </Box>

      {/* Right Section */}
      <Box display="flex" alignItems="center">
        <IconButton onClick={colorMode.toggleColorMode}>
          {theme.palette.mode === "dark" ? (
            <DarkModeOutlinedIcon />
          ) : (
            <LightModeOutlinedIcon />
          )}
        </IconButton>
        <IconButton>
          <HomeOutlinedIcon
            onClick={goToModulesLandingPage}
            style={{ cursor: "pointer" }}
          />
        </IconButton>
        <IconButton>
          <NotificationsOutlinedIcon />
        </IconButton>
        <IconButton>
          <SettingsOutlinedIcon />
        </IconButton>
        <IconButton onClick={toggleUserMenu}>
          <PersonOutlinedIcon />
        </IconButton>
        <IconButton>MINT</IconButton>
        <IconButton onClick={toggleSideNav}>
          {isSideNavOpen ? <CloseIcon /> : <MenuIcon />}
        </IconButton>
      </Box>

      <SideNav
        ref={sideNavRef}
        isOpen={isSideNavOpen}
        onClose={() => setIsSideNavOpen(false)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        selectedRole={selectedRole}
      />
      <UserMenu
        ref={userMenuRef}
        isOpen={isUserMenuOpen}
        onClose={() => setIsUserMenuOpen(false)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      />
    </Box>
  );
};

export default Topbar;
